// 전역 검색·점프 — Cmd+K / Ctrl+K로 열림. 모든 페이지로 fuzzy 점프 + 자주쓰는 액션.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useT } from '../lib/i18n'
import type { TranslationKey } from '../locales/ko'

type Item = { id: string; labelKey: TranslationKey; icon: any; group: string; to?: string; action?: () => void }

function fuzzyScore(query: string, text: string): number {
  if (!query) return 0
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t.startsWith(q)) return 1000 - text.length
  if (t.includes(q)) return 500 - text.length
  // 한글 초성 매칭 (간단)
  let qi = 0
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++
  }
  return qi === q.length ? 100 - text.length : -1
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const navigate = useNavigate()
  const { t } = useT()

  const items: Item[] = useMemo(() => [
    { id: 'dashboard',         labelKey: 'nav.dashboard',       icon: 'gauge',                    group: 'nav.group.operations', to: '/dashboard' },
    { id: 'production',        labelKey: 'nav.production',      icon: 'clipboard-list',           group: 'nav.group.operations', to: '/production-orders' },
    { id: 'work-orders',       labelKey: 'nav.workOrders',      icon: 'industry',                 group: 'nav.group.operations', to: '/work-orders' },
    { id: 'equipment-data',    labelKey: 'nav.equipmentData',   icon: 'microchip',                group: 'nav.group.operations', to: '/equipment-data' },
    { id: 'inspection',        labelKey: 'nav.inspection',      icon: 'magnifying-glass-chart',   group: 'nav.group.quality',    to: '/inspection' },
    { id: 'spc',               labelKey: 'nav.spc',             icon: 'chart-area',               group: 'nav.group.quality',    to: '/spc' },
    { id: 'lots',              labelKey: 'nav.lots',            icon: 'sitemap',                  group: 'nav.group.quality',    to: '/lots' },
    { id: 'andon',             labelKey: 'nav.andon',           icon: 'tower-observation',        group: 'nav.group.equipment',  to: '/andon' },
    { id: 'equipment-status',  labelKey: 'nav.equipmentStatus', icon: 'chart-line',               group: 'nav.group.equipment',  to: '/equipment-status' },
    { id: 'maintenance',       labelKey: 'nav.maintenance',     icon: 'wrench',                   group: 'nav.group.equipment',  to: '/maintenance' },
    { id: 'schedule',          labelKey: 'nav.schedule',        icon: 'calendar-days',            group: 'nav.group.planning',   to: '/schedule' },
    { id: 'forecasting',       labelKey: 'nav.forecasting',     icon: 'robot',                    group: 'nav.group.planning',   to: '/forecasting' },
    { id: 'cost',              labelKey: 'nav.cost',            icon: 'money-bill-trend-up',      group: 'nav.group.planning',   to: '/cost' },
    { id: 'boms',              labelKey: 'nav.boms',            icon: 'list-check',               group: 'nav.group.master',     to: '/boms' },
    { id: 'labor',             labelKey: 'nav.labor',           icon: 'users',                    group: 'nav.group.master',     to: '/labor' },
    { id: 'inventory',         labelKey: 'nav.inventory',       icon: 'warehouse',                group: 'nav.group.master',     to: '/inventory' },
    { id: 'audit',             labelKey: 'nav.audit' as TranslationKey, icon: 'shield-halved',    group: 'nav.group.master',     to: '/audit' },
    { id: 'account',           labelKey: 'nav.account',         icon: 'user',                     group: 'nav.group.master',     to: '/account' },
  ], [])

  const filtered = useMemo(() => {
    if (!query.trim()) return items
    return items
      .map((it) => ({ it, score: fuzzyScore(query, t(it.labelKey)) }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.it)
  }, [items, query, t])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.includes('Mac')
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    setTimeout(() => inputRef.current?.focus(), 30)
  }, [open])

  useEffect(() => { setActive(0) }, [query])

  const choose = (it: Item) => {
    setOpen(false)
    if (it.to) navigate(it.to)
    it.action?.()
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="명령 팔레트"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        display: 'grid', placeItems: 'start center',
        paddingTop: '15vh',
      }}
    >
      <div
        className="glass"
        style={{
          width: 'min(560px, 92vw)',
          maxHeight: '60vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1rem', borderBottom: '1px solid var(--color-line)' }}>
          <FontAwesomeIcon icon="magnifying-glass-chart" aria-hidden="true" style={{ opacity: 0.6 }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)) }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
              else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) choose(filtered[active]) }
            }}
            placeholder="페이지 또는 액션 검색…"
            style={{
              flex: 1, border: 0, outline: 'none', background: 'transparent',
              font: 'inherit', fontSize: 'var(--fs-md)', color: 'inherit',
            }}
            aria-controls="cmd-list"
            aria-activedescendant={filtered[active] ? `cmd-${filtered[active].id}` : undefined}
          />
          <kbd style={{ fontSize: 'var(--fs-xs)', padding: '0.2rem 0.45rem', border: '1px solid var(--color-line)', borderRadius: 4, opacity: 0.6 }}>ESC</kbd>
        </div>
        <ul id="cmd-list" role="listbox" style={{ overflow: 'auto', listStyle: 'none', margin: 0, padding: '0.4rem' }}>
          {filtered.length === 0 ? (
            <li className="muted" style={{ padding: '1.5rem', textAlign: 'center' }}>결과 없음</li>
          ) : filtered.map((it, i) => (
            <li
              id={`cmd-${it.id}`}
              key={it.id}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(it) }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.7rem',
                padding: '0.65rem 0.85rem', borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: i === active ? 'var(--color-line)' : 'transparent',
              }}
            >
              <FontAwesomeIcon icon={it.icon} style={{ opacity: 0.7, width: 18 }} aria-hidden="true" />
              <span style={{ flex: 1 }}>{t(it.labelKey)}</span>
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t(it.group as TranslationKey)}</span>
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 1rem', borderTop: '1px solid var(--color-line)', fontSize: 'var(--fs-xs)', opacity: 0.6 }}>
          <span><kbd>↑↓</kbd> 선택</span>
          <span><kbd>Enter</kbd> 열기</span>
          <span style={{ marginLeft: 'auto' }}><kbd>⌘K</kbd> 토글</span>
        </div>
      </div>
    </div>
  )
}
